import { Link, useNavigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
    ChangeEvent, ReactElement, useEffect, useState,
} from 'react';
import {
    Alert,
    Button,
    Modal,
    Row,
    Col,
    Table,
} from 'react-bootstrap';
import useAccountContext from '../../../authentication/hooks';
import BlockUISpinner from '../../BlockUISpinner';
import PrimaryButton from '../../Buttons/PrimaryButton';
import ButtonGroup from '../../Buttons/ButtonGroup';
import { OrganisationDto, OrganisationsClient, UsersClient } from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import NotificationMessage from '../../Alert/NotificationMessage';
import { clearBranchModalNotification, getBranchModalNotification } from '../../../storage/notification';
import AppLogger from '../../../instrumentation/AppLogger';

interface SavingBranchSelectorErrorProps {
    showError: boolean,
}

const showBranchModalMessage = (message: JSX.Element | null) => (
    <>
        {message && (
            <Row className='mb-4'>
                <Col>
                    {message}
                </Col>
            </Row>
        )}
    </>
);

const setNotification = () => {
    const branchModalNotification = getBranchModalNotification();
    return (
        branchModalNotification
            ? (
                <NotificationMessage
                    id='notif-message-1'
                    canClose
                    onClose={clearBranchModalNotification}
                    {...branchModalNotification}
                />
            )
            : null);
};

const SavingBranchSelectorError = (props: SavingBranchSelectorErrorProps) : ReactElement | null => {
    const { showError } = props;
    if (showError) {
        return (
            <Row className='mb-4'>
                <Col>
                    <Alert variant='danger' className='d-flex'>
                        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
                            <div className='bgCircle me-3'>
                                <i className='icon-warning' aria-hidden='true' />
                            </div>
                        </div>
                        <div>
                            <p className='mb-3'>Error trying to save default branch/location. Try again later!</p>
                        </div>
                    </Alert>
                </Col>
            </Row>
        );
    }
    return null;
};

interface SaveButtonProps {
    onClick: () => void,
}

const SaveButton = (props: SaveButtonProps) : JSX.Element => {
    const { onClick } = props;
    return (
        <PrimaryButton
            data-testid='save-button'
            onClick={onClick}
            className='ms-md-auto'
        >
            Save and close
        </PrimaryButton>
    );
};

interface BranchSelectorButtonsProps {
    left: any,
    right: any
}

const BranchSelectorButtons = (props: BranchSelectorButtonsProps) => {
    const { left, right } = props;
    return (
        <ButtonGroup
            left={left}
            right={right}
        />
    );
};

const BranchSelectorModal = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isModalDataLoading, setIsModalDataLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savingBranchSelectorError, setSavingBranchSelectorError] = useState(false);
    const { inProgress, accounts, instance } = useMsal();
    const account = useAccountContext();
    const [selectedBranch, setSelectedBranch] = useState<number | undefined>();
    const [selectedOrganisation, setSelectedOrganisation] = useState<string | undefined>();
    const [selectedTradingName, setSelectedTradingName] = useState<string | undefined>();
    const [selectedBranchName, setSelectedBranchName] = useState<string | undefined>();
    const [selectedABN, setSelectedABN] = useState<string | undefined>();
    const [selectedCRMGuid, setSelectedCRMGuid] = useState<string | undefined>();
    const [branches, setBranches] = useState<OrganisationDto[] | undefined>();
    const branchModalMessage = setNotification();
    const accountContext = useAccountContext();

    const defaultOrganisationIdSet = account?.details?.defaultOrganisationId !== null;

    const navigate = useNavigate();
    const locationOnModalSave = '/';

    const onContinueBranchSelectorModal = async () => {
        if (inProgress === InteractionStatus.None && accounts.length > 0) {
            setIsSaving(true);
            const client = new UsersClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            try {
                await client.setDefaultOrganisation({ defaultOrganisationId: selectedBranch });
                if (account) {
                    await account.setDefaultOrganisationId(selectedBranch, selectedCRMGuid);
                    if (selectedOrganisation !== undefined) {
                        await account.setOrganisationAndBranch(selectedOrganisation, selectedTradingName !== undefined ? selectedTradingName : '', selectedBranchName !== undefined ? selectedBranchName : '');
                    }
                }
                if (!account?.details?.accountCreationCompleted) {
                    account?.setCompleted();
                    account?.setDefaultOrganisationId(selectedBranch, selectedCRMGuid);
                    account?.setTargetOrganisation(selectedOrganisation!, selectedABN!);
                }
                account?.setShowBranchSelector(false);
            } catch (error) {
                AppLogger.error('Failed to select organisation', error as Error);
                setSavingBranchSelectorError(true);
            } finally {
                setIsSaving(false);
                account?.setShowBranchSelector(false);
                // Reload dashboard page to grab new accountContext etc details
                navigate(locationOnModalSave);
            }
            setIsSaving(false);
            account?.setShowBranchSelector(false);
            clearBranchModalNotification();
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>, branchId: number | undefined, organisationName: string | undefined, tradingName: string | undefined, branchName: string | undefined, abn: string | undefined, crmGuid: string | undefined) => {
        setSelectedBranch(branchId);
        setSelectedOrganisation(organisationName);
        setSelectedTradingName(tradingName);
        setSelectedBranchName(branchName);
        setSelectedABN(abn);
        setSelectedCRMGuid(crmGuid);
    };

    useEffect(() => {
        const loadBranches = async () => {
            if (account
                && account.details
                && account.details.abn) {
                const client = new OrganisationsClient();
                const tokenResult = await instance.acquireTokenSilent({
                    ...tokenRequest,
                    account: accounts[0],
                });
                client.setAuthToken(tokenResult.accessToken);

                try {
                    setIsModalDataLoading(true); // Modal data
                    const result = await client.getOrganisationsByABN(account.details.abn, true);
                    setBranches(result);
                    if (account.details.defaultOrganisationId !== null) {
                        setSelectedBranch(account.details.defaultOrganisationId);
                        setSelectedCRMGuid(account.details.organisationCRMGuid);
                    } else {
                        setSelectedBranch(result[0].organisationId);
                        setSelectedOrganisation(result[0].name);
                        setSelectedTradingName(result[0].businessOrTradingName);
                        setSelectedBranchName(result[0].branchOrLocationName);
                        setSelectedABN(result[0].abn);
                        setSelectedCRMGuid(result[0].crmGuid);
                    }
                } catch (error) {
                    AppLogger.error('Failed to load branches', error as Error);
                    setSavingBranchSelectorError(true);
                } finally {
                    setIsModalDataLoading(false);
                }
                setIsModalDataLoading(false);
            }
        };
        loadBranches();
    }, [account, accounts, instance, accountContext, accountContext?.details?.defaultOrganisationId]);

    const handleClose = () => {
        account?.setShowBranchSelector(false);
        clearBranchModalNotification();
    };

    const saveButton = () => (
        <SaveButton onClick={onContinueBranchSelectorModal} />
    );

    const closeButton = () => (
        <Button
            data-testid='exit-portal-button'
            onClick={handleClose}
            variant='tertiary'
            className='me-md-auto order-2 order-md-0'
        >
            <i className='icon-close me-1' aria-hidden='true' />
            Cancel
        </Button>
    );

    return (
        <Modal
            size='lg'
            show={account?.details?.showBranchSelector}
            aria-modal={account?.details?.showBranchSelector}
            enforceFocus={account?.details?.showBranchSelector}
            aria-live='assertive'
            aria-atomic='true'
            tabIndex={-1}
            backdrop='static'
            keyboard={false}
            data-testid='prompt-branchselector-modal'
            // aria-labelledby='modal-select-branch'
            onHide={handleClose}
        >
            <Modal.Header closeButton={defaultOrganisationIdSet}>
                <Modal.Title
                    id='modal-select-branch'
                    as='h3'
                >
                    {'Add or manage branch/location '}
                    <span className='visually-hidden'> (modal dialog)</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className='mb-3'>
                    <Col>
                        <p>
                            Branches or locations can be created and may be used to identify and manage physical office
                            branches or business divisions within your organisation.
                            E.g. &quot;ACME Ltd, Engineering Department&quot; or &quot;Cyberdyne Robotics, Port Melbourne Office&quot;.
                        </p>
                        <p>
                            The branch or location name can be used to help you group your NMI Services portal requests for simplified management.
                        </p>
                    </Col>
                </Row>
                <SavingBranchSelectorError showError={savingBranchSelectorError} />
                {showBranchModalMessage(branchModalMessage)}
                <Row className='mb-4'>
                    <Col>
                        <div
                            aria-labelledby='caption-org-select-list'
                        >
                            <fieldset
                                className='w-100'
                                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                                role='radiogroup'
                            >
                                <legend className='h4 mb-3'>Select an existing branch/location name to manage from the list or add a new branch/location.</legend>
                                <Table
                                    id='select-org-table'
                                    data-testid='select-org-table'
                                    className='table-sm table-responsive-stack small mb-0'
                                    aria-live='off'
                                >
                                    <caption id='caption-org-select-list' className='visually-hidden'>
                                        Add, edit or choose your default branch/location from the table list for this organisation.
                                    </caption>
                                    <thead key='headKeyForThead'>
                                        <tr className='table-active'>
                                            {/* <th scope='col'><span className='visually-hidden'>Set as default</span></th>
                                            <th scope='col' className='text-nowrap'>Branch/location name</th> */}
                                            <th scope='col' className='w-50'>
                                                <span className='visually-hidden text-nowrap'>Set as default</span>
                                                <span className='ms-2'>Branch/location name</span>
                                            </th>
                                            <th scope='col' className='w-50 text-nowrap'>Location</th>
                                            <th scope='col' className='text-nowrap'>Action</th>
                                        </tr>
                                    </thead>
                                    {isModalDataLoading
                                        ? (
                                            <tbody key='tBodyKey'>
                                                <tr>
                                                    <td colSpan={3}>
                                                        <BlockUISpinner partial>
                                                            <p>Loading data...</p>
                                                        </BlockUISpinner>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        )
                                        : (
                                            <>
                                                {branches !== undefined && branches?.length > 0 ? (
                                                    <tbody key='tBodyKey'>
                                                        {branches.map((branch, index) => (
                                                            <tr key={branch.organisationId}>
                                                                <td className='pe-0' data-header='Set as default'>
                                                                    {/* To do - replace this with RadioButton component */}
                                                                    <div className='form-field-container mb-0' tabIndex={-1}>
                                                                        <div className='radio-button'>
                                                                            <input
                                                                                type='radio'
                                                                                id={`branch${index}`}
                                                                                name='branchSelector'
                                                                                value={branch.organisationId}
                                                                                checked={branch.organisationId === selectedBranch}
                                                                                onChange={
                                                                                    (e) => handleChange(e, branch.organisationId, branch.name, branch.businessOrTradingName, branch.branchOrLocationName, branch.abn, branch.crmGuid)
                                                                                }
                                                                            />
                                                                            <label htmlFor={`branch${index}`} className='form-field flex-column' title='Set as default'>
                                                                                {/* <span className='visually-hidden'>
                                                                                    {'Set as default '}
                                                                                </span> */}
                                                                                <span className='mb-2 text-break'>{branch.name}</span>
                                                                                <span className='text-break'>
                                                                                    {!!(branch.businessOrTradingName) && (`${branch.businessOrTradingName} `)}
                                                                                    {!!(branch.branchOrLocationName) && (`- ${branch.branchOrLocationName}`)}
                                                                                </span>
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td data-header='Location' className='text-break'>
                                                                    <span>
                                                                        {`${branch.streetAddress?.suburb}, ${branch.streetAddress?.state}`}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <Link
                                                                        to={`/update-organisation/${branch.organisationId}`}
                                                                        onClick={handleClose}
                                                                        aria-label='Edit details for this branch/location'
                                                                    >
                                                                        Edit
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                ) : (
                                                    <tbody key='tBodyKey'>
                                                        <tr>
                                                            <td colSpan={3}>No branch/locations available. Please create a new one.</td>
                                                        </tr>
                                                    </tbody>
                                                )}
                                            </>
                                        )}

                                </Table>
                            </fieldset>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Link
                            data-testid='open-branch-add-button'
                            to='/add-branch'
                            className='btn btn-flat btn-tertiary'
                            onClick={handleClose}
                        >
                            <i className='icon-add fs-2 me-2' aria-hidden='true' />
                            Add branch or location
                        </Link>
                    </Col>
                </Row>
                {isLoading && (
                    <BlockUISpinner>
                        <p>Loading...</p>
                    </BlockUISpinner>
                )}
                {isSaving && (
                    <BlockUISpinner>
                        <p>Saving...</p>
                    </BlockUISpinner>
                )}
            </Modal.Body>
            <Modal.Footer className='d-inline'>
                <BranchSelectorButtons
                    left={() => defaultOrganisationIdSet && closeButton()}
                    right={() => saveButton()}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default BranchSelectorModal;
